import React, { useEffect, useState } from 'react';

import { buscarMotivosDeParadaAtivos, type MotivoDeParadaAtivo } from '../../api/motivosDeParadaApi';
import { notify } from '../../shared/feedback/notify';
import '../../shared/feedback/erro-de-tela.css';

/**
 * O motivo da parada do serviço (at-134, ADR 0065) — o MESMO campo nos três pontos em que um
 * serviço é deixado inacabado: o "Finalizar atividade" do colaborador e os modais "Alterar
 * esteira" e "Aprovar esteira" do Chefe. Um componente só, para os três dizerem a mesma coisa.
 *
 * Três estados da lista, e nenhum deles pode se parecer com outro:
 * - **com motivos**: a escolha é obrigatória (o backend recusa sem ela), e o texto vira o
 *   DETALHE — obrigatório só no motivo que o exige ("Outro");
 * - **lista vazia** (ninguém cadastrou motivos): o campo de texto livre de antes, opcional — é o
 *   bootstrap inerte do catálogo;
 * - 🔴 **falha ao carregar**: a escolha fica BLOQUEADA, com a explicação. Cair no texto livre
 *   mandaria um pedido sem motivo que o backend recusaria — e, no "Finalizar atividade", a
 *   mudança de esteira já teria sido pedida antes da recusa (at-60).
 *
 * Sem CSS próprio: estilos inline com os tokens de tema, para servir aos três modais sem criar
 * arquivo novo nem depender do CSS de nenhum deles.
 */

export type EstadoDosMotivos = 'carregando' | 'falhou' | 'pronto';

export function useMotivosDaParada(habilitado: boolean) {
  const [motivos, setMotivos] = useState<MotivoDeParadaAtivo[]>([]);
  const [estado, setEstado] = useState<EstadoDosMotivos>('carregando');

  useEffect(() => {
    if (!habilitado) return undefined;
    let vivo = true;
    setEstado('carregando');
    buscarMotivosDeParadaAtivos()
      .then((lista) => {
        if (!vivo) return;
        setMotivos(lista);
        setEstado('pronto');
      })
      .catch((erro) => {
        if (!vivo) return;
        setMotivos([]);
        setEstado('falhou');
        notify.errorFrom(
          erro,
          'Não foi possível carregar os motivos da parada. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'motivos-da-parada' },
        );
      });
    return () => {
      vivo = false;
    };
  }, [habilitado]);

  return { motivos, estado };
}

/**
 * O que impede de confirmar a parada, por extenso — ou `null` quando está tudo certo. A tela usa
 * o texto para desabilitar o botão E dizer por quê.
 */
export function pendenciaDoMotivo(
  estado: EstadoDosMotivos,
  motivos: MotivoDeParadaAtivo[],
  motivoId: string,
  detalhe: string,
): string | null {
  if (estado === 'carregando') return 'Carregando os motivos da parada...';
  if (estado === 'falhou') {
    return 'Não foi possível carregar os motivos da parada. Recarregue a página para deixar o serviço inacabado.';
  }
  if (motivos.length === 0) return null;
  const escolhido = motivos.find((m) => m.id === motivoId);
  if (!escolhido) return 'Escolha o motivo da parada.';
  if (escolhido.exigeTexto && !detalhe.trim()) return `Descreva o detalhe para o motivo "${escolhido.nome}".`;
  return null;
}

/**
 * O que os modais do Chefe devolvem ao hospedeiro: só quando o desfecho é PAUSADO e há o que
 * mandar. Com o catálogo vazio e sem detalhe, `undefined` — a chamada fica igual à de antes.
 */
export function motivoDaParadaParaEnviar(
  desfecho: 'CONCLUIDO' | 'PAUSADO' | null | undefined,
  motivoId: string,
  detalhe: string,
): { motivoParadaId?: string; pausaObservacao?: string } | undefined {
  if (desfecho !== 'PAUSADO') return undefined;
  const pausaObservacao = detalhe.trim() || undefined;
  const motivoParadaId = motivoId || undefined;
  return motivoParadaId || pausaObservacao ? { motivoParadaId, pausaObservacao } : undefined;
}

interface MotivoDaParadaProps {
  /** Prefixo dos ids dos campos (dois modais podem estar montados ao mesmo tempo). */
  idPrefixo: string;
  motivos: MotivoDeParadaAtivo[];
  estado: EstadoDosMotivos;
  motivoId: string;
  onMotivo: (id: string) => void;
  detalhe: string;
  onDetalhe: (texto: string) => void;
}

const estiloRotulo: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: 'var(--tema-texto-3, #64748b)',
  marginBottom: 4,
};

const estiloCampo: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--tema-linha-2, #cbd5e1)',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
  background: 'var(--tema-superficie, #fff)',
  color: 'var(--tema-texto, #1a1c1b)',
};

const MotivoDaParada: React.FC<MotivoDaParadaProps> = ({
  idPrefixo,
  motivos,
  estado,
  motivoId,
  onMotivo,
  detalhe,
  onDetalhe,
}) => {
  if (estado === 'carregando') {
    return <p style={{ ...estiloRotulo, marginTop: 10 }}>Carregando os motivos da parada...</p>;
  }

  if (estado === 'falhou') {
    return (
      <p className="erro-de-tela" role="alert" style={{ marginTop: 10 }}>
        Não foi possível carregar os motivos da parada. Recarregue a página para deixar o serviço inacabado.
      </p>
    );
  }

  const escolhido = motivos.find((m) => m.id === motivoId);
  const comLista = motivos.length > 0;

  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {comLista && (
        <div>
          <label htmlFor={`${idPrefixo}-motivo`} style={estiloRotulo}>
            Motivo da parada
          </label>
          <select
            id={`${idPrefixo}-motivo`}
            value={motivoId}
            onChange={(e) => onMotivo(e.target.value)}
            style={estiloCampo}
          >
            <option value="">Escolha o motivo</option>
            {motivos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nome}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label htmlFor={`${idPrefixo}-detalhe`} style={estiloRotulo}>
          {!comLista ? 'Motivo (opcional)' : escolhido?.exigeTexto ? 'Detalhe (obrigatório)' : 'Detalhe (opcional)'}
        </label>
        <input
          id={`${idPrefixo}-detalhe`}
          type="text"
          maxLength={200}
          value={detalhe}
          onChange={(e) => onDetalhe(e.target.value)}
          placeholder="Ex: aguardando a chegada do para-choque"
          style={estiloCampo}
        />
      </div>
    </div>
  );
};

export default MotivoDaParada;
